import { useState, useEffect, useCallback } from 'react';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { unwrapTenderEvaluationList } from '../utils/tenderEvaluationHelpers';

export const buildTenderEvaluationListUrl = ({
  currentPage,
  rowsPerPage,
  procurementTypeHeader,
  search,
  statusHps,
  procurementType,
  contractType,
  sortField,
  sortDirection,
}) => {
  const params = new URLSearchParams();
  params.set('PageNumber', String(currentPage));
  params.set('PageSize', String(rowsPerPage));

  if (procurementTypeHeader) {
    params.set('ProcurementTypeHeader', procurementTypeHeader);
  }

  if (search?.trim()) {
    params.set('Search', search.trim());
  }

  if (statusHps && statusHps !== 'all') {
    params.set('StatusHps', statusHps);
  }

  if (procurementType) {
    params.set('ProcurementType', procurementType);
  }

  if (contractType) {
    params.set('ContractType', contractType);
  }

  if (sortField) {
    params.set('SortBy', sortField);
    params.set('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
  }

  return `${api.API_URL}/procurement/get-all-tender-evaluation?${params.toString()}`;
};

const useTenderEvaluationList = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  enabled = true,
}) => {
  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [procurementTypeFilter, setProcurementTypeFilter] = useState('');
  const [contractTypeFilter, setContractTypeFilter] = useState('');
  const [sortField, setSortField] = useState('procurementNumber');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsLoading(true);

    try {
      const url = buildTenderEvaluationListUrl({
        currentPage,
        rowsPerPage,
        procurementTypeHeader,
        search: debouncedSearchText,
        statusHps: statusFilter,
        procurementType: procurementTypeFilter,
        contractType: contractTypeFilter,
        sortField,
        sortDirection,
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      const { items, totalItems } = unwrapTenderEvaluationList(result);

      setData({ items });
      setTotalRows(totalItems);
    } catch (error) {
      notify.apiError(error, 'load tender evaluation data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    statusFilter,
    procurementTypeFilter,
    contractTypeFilter,
    sortField,
    sortDirection,
    procurementTypeHeader,
    enabled,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  const handleSearch = e => setSearchText(e.target.value);

  const handleStatusFilter = value => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleProcurementTypeFilter = value => {
    setProcurementTypeFilter(value);
    setCurrentPage(1);
  };

  const handleContractTypeFilter = value => {
    setContractTypeFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = page => setCurrentPage(page);

  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleSort = (column, direction) => {
    const fieldMapping = {
      idProc: 'procurementNumber',
      title: 'titleProcurement',
      procType: 'procurementType',
      description: 'description',
      status: 'statusHps',
    };
    setSortField(fieldMapping[column.id] || column.id || 'procurementNumber');
    setSortDirection(direction);
  };

  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setProcurementTypeFilter('');
    setContractTypeFilter('');
    setSortField('procurementNumber');
    setSortDirection('desc');
    setCurrentPage(1);
  };

  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  return {
    data,
    totalRows,
    currentPage,
    rowsPerPage,
    searchText,
    statusFilter,
    procurementTypeFilter,
    contractTypeFilter,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handleProcurementTypeFilter,
    handleContractTypeFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
    debouncedSearchText,
    sortField,
    sortDirection,
    refresh: fetchData,
  };
};

export default useTenderEvaluationList;
